import { useCallback, useEffect, useMemo, useState } from "react";
import { listarCajasReporte, obtenerReporteCaja } from "../../servicios/cajasApi";
import { extraerCajas, fechaCajaParaInput, formatearFechaCaja, normalizarReporteCaja } from "../../servicios/cajas";
import { obtenerContextoSesion } from "../../servicios/contextoSesion";
import { useStoreHome } from "../Home/useStoreHome";
import "./cajaReporte.css";

const dinero = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });

export default function CajaReporte() {
  const empresaId = useStoreHome((state) => state.EmpresaId);
  const hoy = fechaCajaParaInput();
  const [filtros, setFiltros] = useState({ cajaId: "", fechaDesde: hoy, fechaHasta: hoy });
  const [cajas, setCajas] = useState([]);
  const [reporte, setReporte] = useState({ ingresos: 0, egresos: 0, saldoNeto: 0, lista: [], error: "" });
  const [cargando, setCargando] = useState(false);

  const consultar = useCallback(async (proximos = filtros) => {
    setCargando(true);
    const respuesta = await obtenerReporteCaja({
      EmpresaId: empresaId,
      cajaId: proximos.cajaId ? Number(proximos.cajaId) : null,
      fechaDesde: `${proximos.fechaDesde}T00:00:00`,
      fechaHasta: `${proximos.fechaHasta}T00:00:00`,
    });
    setReporte(normalizarReporteCaja(respuesta));
    setCargando(false);
  }, [empresaId, filtros]);

  useEffect(() => {
    if (!empresaId) return;
    listarCajasReporte({ EmpresaId: empresaId, activo: null }).then((respuesta) => setCajas(extraerCajas(respuesta)));
    consultar();
    // La consulta inicial usa los filtros creados para el día local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  const porFormaPago = useMemo(() => Object.values(reporte.lista.reduce((grupos, movimiento) => {
    const clave = movimiento.formaPago || "Sin medio informado";
    grupos[clave] ||= { nombre: clave, total: 0, cantidad: 0 };
    grupos[clave].total += movimiento.importe;
    grupos[clave].cantidad += 1;
    return grupos;
  }, {})).sort((a, b) => Math.abs(b.total) - Math.abs(a.total)), [reporte.lista]);

  const buscar = (event) => { event.preventDefault(); consultar(filtros); };
  const ctx = obtenerContextoSesion({ EmpresaId: empresaId });

  return <main className="caja-reporte">
    <header><div><span>Administración</span><h1>Movimientos de caja</h1><p>Consulta por fecha independiente de la apertura o cierre de la sesión.</p></div><small>PC #{ctx.pcId || "—"}</small></header>
    <form className="caja-reporte-filtros" onSubmit={buscar}>
      <label><span>Caja</span><select value={filtros.cajaId} onChange={(event) => setFiltros({ ...filtros, cajaId: event.target.value })}><option value="">Todas las cajas</option>{cajas.map((caja) => <option key={caja.id} value={caja.id}>{caja.codigo} · {caja.descripcion}</option>)}</select></label>
      <label><span>Desde</span><input required type="date" value={filtros.fechaDesde} onChange={(event) => setFiltros({ ...filtros, fechaDesde: event.target.value })} /></label>
      <label><span>Hasta</span><input required type="date" value={filtros.fechaHasta} onChange={(event) => setFiltros({ ...filtros, fechaHasta: event.target.value })} /></label>
      <button disabled={cargando}><i className={`pi ${cargando ? "pi-spin pi-spinner" : "pi-search"}`} /> {cargando ? "Consultando..." : "Consultar"}</button>
    </form>
    {reporte.error && <div className="caja-reporte-error" role="alert"><i className="pi pi-exclamation-circle" /> {reporte.error}</div>}
    <section className="caja-reporte-kpis" aria-label="Totales del período">
      <article><span>Ingresos</span><strong>{dinero.format(reporte.ingresos)}</strong></article>
      <article><span>Egresos</span><strong>{dinero.format(reporte.egresos)}</strong></article>
      <article className="destacado"><span>Movimiento neto</span><strong>{dinero.format(reporte.saldoNeto)}</strong></article>
      <article><span>Movimientos</span><strong>{reporte.lista.length}</strong></article>
    </section>
    {porFormaPago.length > 0 && <section className="caja-reporte-medios"><h2>Por medio de pago</h2><div>{porFormaPago.map((forma) => <article key={forma.nombre}><span>{forma.nombre}<small>{forma.cantidad} movimiento(s)</small></span><strong>{dinero.format(forma.total)}</strong></article>)}</div></section>}
    <section className="caja-reporte-resultados"><header><div><small>Detalle</small><h2>Movimientos del período</h2></div><span>{reporte.lista.length} resultado(s)</span></header>
      {cargando && !reporte.lista.length ? <div className="caja-reporte-vacio"><i className="pi pi-spin pi-spinner" /> Consultando movimientos...</div>
        : !reporte.lista.length ? <div className="caja-reporte-vacio"><i className="pi pi-inbox" /><strong>Sin movimientos</strong><span>No se registraron movimientos para los filtros elegidos.</span></div>
          : <div className="caja-reporte-tabla"><table><thead><tr><th>Fecha</th><th>Caja</th><th>Tipo</th><th>Medio</th><th>Concepto</th><th>Operador</th><th className="numero">Importe</th></tr></thead><tbody>{reporte.lista.map((movimiento) => <tr key={movimiento.id}><td>{formatearFechaCaja(movimiento.fecha)}</td><td><strong>{movimiento.cajaCodigo}</strong><small>{movimiento.cajaDescripcion}</small></td><td>{movimiento.tipo}</td><td>{movimiento.formaPago || "—"}</td><td>{movimiento.concepto || "—"}</td><td>#{movimiento.operadorId}</td><td className={`numero ${movimiento.importe < 0 ? "negativo" : ""}`}><strong>{dinero.format(movimiento.importe)}</strong></td></tr>)}</tbody></table></div>}
    </section>
  </main>;
}
